import React, {Component, ReactNode} from 'react';
import {MuiPickersUtilsProvider, DatePicker, Day} from '@material-ui/pickers';
import MomentUtils  from '@date-io/moment';
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';
import {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';
import {DateTime, TimeZone} from 'util/time';
import moment, {Moment} from 'moment-timezone';
import './index.scss';

interface DateFieldProps extends ValidatedFieldProps {
  label?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: (value:DateTime|null)=>void;
  className?: string;
  inline?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  defaultValue?: DateTime;
  timeZone?: TimeZone;
  placeholder?: string;
  startAdornment?: ReactNode,
  endAdornment?: ReactNode,
  closeOnSelect?: boolean
}

interface DateFieldState extends ValidatedFieldState {
  value: DateTime|null;
  currentDate: Moment;
  displayedDate: Moment;
}

export default class DateField extends Component<DateFieldProps, DateFieldState> {
  state: DateFieldState = {
    dirty: false,
    touched: false,
    validationMessage: undefined,
    value: null,
    currentDate: moment(),
    displayedDate: moment()
  };

  componentDidMount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
    let value: DateTime|null = this.props.model && this.props.name ? this.props.model[this.props.name] || null : null;
    if(!value) {
      value = this.props.defaultValue || null;
    }
    let displayedDate = value ? value.innerValue : moment.tz();
    this.setState({
      currentDate: this.props.timeZone ? moment.tz(this.props.timeZone) : moment.tz(),
      displayedDate: this.props.timeZone ? displayedDate.tz(this.props.timeZone) : displayedDate,
      value: value
    });
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
  }

  shouldComponentUpdate(nextProps: Readonly<DateFieldProps>, nextState: Readonly<DateFieldState>, nextContext: any): boolean {
    for(const key of Object.keys(nextProps)) {
      switch(key) {
        case 'onChange':
          break; // ignore
        default:
          if((nextProps as any)[key] !== (this.props as any)[key]) {
            return true;
          }
          break;
      }
    }
    const value = this.state.value;
    const newValue = nextProps.model && nextProps.name ? nextProps.model[nextProps.name] : null;
    if(!!value !== !!newValue || (value && !value.equals(newValue))) {
      return true;
    }
    for(const key of Object.keys(nextState)) {
      if((nextState as any)[key] !== (this.state as any)[key]) {
        return true;
      }
    }
    return false;
  }

  componentDidUpdate(nextProps: Readonly<DateFieldProps>, nextState: Readonly<DateFieldState>, nextContext: any): void {
    let newValue = nextProps.model && nextProps.name ? nextProps.model[nextProps.name] : null;
    if(!newValue && this.props.defaultValue !== nextProps.defaultValue) {
      newValue = this.props.defaultValue;
    }
    const newState: any = {};
    let stateChanged = false;
    let displayedDate = this.state.displayedDate;
    if (newValue && (!this.state.value || !newValue.equals(this.state.value))) {
      newState.value = newValue;
      displayedDate = newValue.innerValue;
      newState.displayedDate = this.props.timeZone ? displayedDate.tz(this.props.timeZone) : displayedDate;
      stateChanged = true;
    }
    if(nextProps.timeZone !== this.props.timeZone) {
      newState.currentDate = this.props.timeZone ? moment.tz(this.props.timeZone) : moment.tz();
      newState.displayedDate = this.props.timeZone ? displayedDate.tz(this.props.timeZone) : displayedDate;
      stateChanged = true;
    }
    if(stateChanged) {
      this.setState(newState);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  onChange = (value: MaterialUiPickersDate) => {
    if(value && this.props.timeZone) {
      value = value.clone().tz(this.props.timeZone!, true);
    }
    const date = value ? new DateTime(value.clone().startOf('day')) : null;
    this.setState({dirty: true, touched: true, value: date}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = date;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
    });
    if(this.props.onChange) {
      this.props.onChange(date);
    }
  };

  renderDay = (day: MaterialUiPickersDate, selectedDate: MaterialUiPickersDate, dayInCurrentMonth: boolean, dayComponent: JSX.Element): JSX.Element => {
    let m = day as Moment;
    let selectedM = selectedDate as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
      selectedM = selectedM.clone().tz(this.props.timeZone, true);
    }
    const displayedDate = this.state.displayedDate;
    const currentDay = this.state.currentDate;
    let disabled = false;
    if(this.props.disableFuture && m.isAfter(currentDay, 'day')) {
      disabled = true;
    }
    if(this.props.disablePast && m.isBefore(currentDay, 'day')) {
      disabled = true;
    }
    return <Day
      selected={m.isSame(selectedM, 'day')}
      current={m.isSame(currentDay, 'day')}
      hidden={!m.isSame(displayedDate, 'month')}
      disabled={disabled}>{m.format("D")}</Day>
  };

  onYearChange = (date: MaterialUiPickersDate) => {
    let m = date as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    this.setState({displayedDate: m});
  };

  onMonthChange = (date: MaterialUiPickersDate) => {
    let m = date as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    this.setState({displayedDate: m});
  };

  shouldDisableDate = (date: MaterialUiPickersDate): boolean => {
    let m = date as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    const currentDay = this.state.currentDate;
    if(this.props.disableFuture && m.isAfter(currentDay, 'day')) {
      return true;
    } else if(this.props.disablePast && m.isBefore(currentDay, 'day')) {
      return true;
    } else {
      return false;
    }
  };

  renderDatePicker(variant: 'inline'|'static', className?: string) {
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const value: DateTime|null = this.state.value;
    const dateValue = value ? value!.toDate() : null;
    const inputProps = {
      startAdornment: this.props.startAdornment,
      endAdornment: this.props.endAdornment
    };
    return <MuiPickersUtilsProvider utils={MomentUtils}>
      <DatePicker
        disableToolbar
        className={className}
        inputVariant="outlined"
        placeholder={this.props.placeholder}
        variant={variant}
        format="DD/MM/yyyy"
        autoOk={this.props.closeOnSelect}
        required={this.props.required}
        disabled={this.props.disabled}
        label={this.props.label}
        value={dateValue}
        error={showError}
        helperText={showError ? this.state.validationMessage : null}
        renderDay={this.renderDay}
        shouldDisableDate={this.shouldDisableDate}
        onChange={this.onChange}
        onYearChange={this.onYearChange}
        onMonthChange={this.onMonthChange}
        InputProps={inputProps}
        PopoverProps={{className: "date-field-popover"}}
      />
    </MuiPickersUtilsProvider>;
  }

  render() {
    let fullClassName = 'date-field';
    if(this.props.className) {
      fullClassName += ' ' + this.props.className;
    }
    if(this.props.disabled) {
      fullClassName += ' disabled';
    }
    if(this.props.inline) {
      return <div className={fullClassName}>{ this.renderDatePicker('static') }</div>;
    } else {
      return this.renderDatePicker('inline', fullClassName);
    }
  }
}